import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../services/contexts/AuthContext";
import firebase from "./firebase";
import "../styles/orders.css";

const ADMIN_EMAIL = "stamsy.art@abv.bg";

const STATUSES = ["all", "pending", "processing", "shipped", "completed", "cancelled"];

const STATUS_LABELS = {
  all: "Всички",
  pending: "Изчакващи",
  processing: "Обработват се",
  shipped: "Изпратени",
  completed: "Завършени",
  cancelled: "Отменени",
};

const formatDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("bg-BG", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export const Orders = () => {
  const { user, authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [savedId, setSavedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [statusEdits, setStatusEdits] = useState({});
  const [saving, setSaving] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.email !== ADMIN_EMAIL) {
      navigate("/");
      return;
    }

    const db = firebase.firestore();
    db.collection("Orders")
      .orderBy("createdAt", "desc")
      .get()
      .then((snap) => {
        const data = [];
        snap.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
        setOrders(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching orders:", err);
        setLoading(false);
      });
  }, [user, authLoading, navigate]);

  const filtered = activeTab === "all" ? orders : orders.filter((o) => o.status === activeTab);

  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);

  const handleStatusChange = (orderId, value) => {
    setStatusEdits((prev) => ({ ...prev, [orderId]: value }));
  };

  const saveStatus = async (orderId) => {
    const newStatus = statusEdits[orderId];
    if (!newStatus) return;
    setSaving((prev) => ({ ...prev, [orderId]: true }));
    try {
      const db = firebase.firestore();
      await db.collection("Orders").doc(orderId).update({ status: newStatus });
      setOrders((prev) => prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o));
      setStatusEdits((prev) => { const n = { ...prev }; delete n[orderId]; return n; });
      setSavedId(orderId);
      setTimeout(() => setSavedId(null), 2000);
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Грешка при записване на статуса");
    }
    setSaving((prev) => { const n = { ...prev }; delete n[orderId]; return n; });
  };

  if (loading) {
    return <div className="orders-page"><p>Зареждане на поръчки...</p></div>;
  }

  return (
    <div className="orders-page">
      <h2>Поръчки</h2>
      <p className="orders-subtitle">{orders.length} общо поръчки</p>

      <div className="orders-tabs">
        {STATUSES.map((s) => (
          <button
            key={s}
            className={`orders-tab${activeTab === s ? " active" : ""}`}
            onClick={() => setActiveTab(s)}
          >
            {STATUS_LABELS[s]}
            {s !== "all" && (
              <span style={{ marginLeft: 6, opacity: 0.7 }}>
                ({orders.filter((o) => o.status === s).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="orders-empty">Няма поръчки в тази категория.</div>
      ) : (
        <div className="orders-list">
          {filtered.map((order) => {
            const isOpen = expandedId === order.id;
            const customer = order.customerInfo || {};
            const currentStatus = statusEdits[order.id] ?? order.status;

            return (
              <div key={order.id} className="order-card">
                <div className="order-card-header" onClick={() => toggleExpand(order.id)}>
                  <div>
                    <div className="order-id">#{order.id.slice(0, 8).toUpperCase()}</div>
                    <div className="order-customer">{customer.name || order.userEmail || "—"}</div>
                  </div>
                  <div className="order-date">{formatDate(order.createdAt)}</div>
                  <span className={`status-badge status-${order.status}`}>
                    {STATUS_LABELS[order.status] || order.status}
                  </span>
                  <div className="order-total">{(order.total || 0).toFixed(2)} €</div>
                </div>

                {isOpen && (
                  <div className="order-card-body">
                    {/* Customer info */}
                    <div>
                      <div className="order-section-title">Клиент</div>
                      <div className="order-info-row">
                        <span className="order-info-label">Име:</span>
                        <span className="order-info-value">{customer.name || "—"}</span>
                      </div>
                      <div className="order-info-row">
                        <span className="order-info-label">Имейл:</span>
                        <span className="order-info-value">{customer.email || order.userEmail || "—"}</span>
                      </div>
                      <div className="order-info-row">
                        <span className="order-info-label">Телефон:</span>
                        <span className="order-info-value">{customer.phone || "—"}</span>
                      </div>
                    </div>

                    {/* Delivery info */}
                    <div>
                      <div className="order-section-title">Доставка</div>
                      <div className="order-info-row">
                        <span className="order-info-label">Начин:</span>
                        <span className="order-info-value">{customer.deliveryMethod || "—"}</span>
                      </div>
                      {customer.city && (
                        <div className="order-info-row">
                          <span className="order-info-label">Град:</span>
                          <span className="order-info-value">{customer.city}</span>
                        </div>
                      )}
                      {customer.address && (
                        <div className="order-info-row">
                          <span className="order-info-label">Адрес:</span>
                          <span className="order-info-value">{customer.address}</span>
                        </div>
                      )}
                      {customer.notes && (
                        <div className="order-info-row">
                          <span className="order-info-label">Бележки:</span>
                          <span className="order-info-value">{customer.notes}</span>
                        </div>
                      )}
                    </div>

                    {/* Items */}
                    <div className="order-items-section">
                      <div className="order-section-title">Продукти</div>
                      {(order.items || []).map((item, i) => (
                        <div key={i} className="order-item-row">
                          <img
                            src={item.img || ""}
                            alt={item.name}
                            className="order-item-img"
                            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                          />
                          <div className="order-item-img" style={{ display: "none", alignItems: "center", justifyContent: "center", background: "#f5f5f5", color: "#ccc", fontSize: 20 }}>🖼</div>
                          <div style={{ flex: 1 }}>
                            <div className="order-item-name">{item.name}</div>
                            <div className="order-item-meta">
                              {item.catalogNumber && <>Кат. №: {item.catalogNumber} · </>}
                              {item.selectedSize && <>Размер: {item.selectedSize} · </>}
                              Бр.: {item.quantity}
                            </div>
                          </div>
                          <div className="order-item-price">
                            {((item.price || 0) * (item.quantity || 1)).toFixed(2)} €
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Status update */}
                    <div className="order-actions">
                      <label>Промени статус:</label>
                      <select
                        className="status-select"
                        value={currentStatus}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      >
                        {STATUSES.filter((s) => s !== "all").map((s) => (
                          <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                        ))}
                      </select>
                      <button
                        className="status-save-btn"
                        onClick={() => saveStatus(order.id)}
                        disabled={saving[order.id] || !statusEdits[order.id]}
                      >
                        {saving[order.id] ? "Записва..." : "Запази"}
                      </button>
                      {savedId === order.id && (
                        <span style={{ color: "#4CAF50", fontWeight: 600, fontSize: 14 }}>✓ Запазено!</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
